import { useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { MkAvatar } from "./MkAvatar";
import { MkButton } from "./MkButton";
import { MkModal } from "./MkModal";

interface Props {
    value?: string | false;
    src?: string | false;
    hasImage?: boolean;
    name?: string;
    title?: string;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    square?: boolean;
    disabled?: boolean;
    onUpload?(dataUrl: string): void;
    onClear?(): void;
}

export function MkAvatarEditor({ value, src, hasImage = false, name, title, size = "xl",
                                 square = false, disabled, onUpload, onClear }: Props) {
    const { t } = useTranslation();
    const input = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);

    const previewSrc = typeof value === "string" && value.startsWith("data:")
        ? value
        : (hasImage && src ? src : "");
    const modalTitle = title || t("mk.avatar.image");

    function pick() {
        if (!disabled) {
            input.current?.click();
        }
    }

    function onFile(event: ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.onload = () => onUpload?.(String(reader.result));
        reader.readAsDataURL(file);
    }

    return (
        <div className={`mk-avatar-editor${disabled ? " is-disabled" : ""}`}>
            <button type="button" className="mk-avatar-editor-btn" title={t("mk.avatar.change")}
                    disabled={disabled} onClick={() => {
                        if (!disabled) {
                            setOpen(true);
                        }
                    }}>
                <MkAvatar size={size} square={square} src={previewSrc || null} name={name || ""} />
                {!disabled && (
                    <span className="mk-avatar-editor-badge">
                        <i className="fa fa-camera" />
                    </span>
                )}
            </button>

            <input type="file" accept="image/*" className="mk-avatar-editor-input"
                   ref={input} onChange={onFile} />

            {open && (
                <MkModal open size="sm" title={modalTitle} icon="fa-picture-o"
                         onClose={() => setOpen(false)}
                         footer={(
                             <>
                                 <MkButton variant="primary" icon="fa-upload"
                                           label={previewSrc ? t("mk.avatar.replace") : t("mk.avatar.upload")}
                                           disabled={disabled} onClick={pick} />
                                 {previewSrc && (
                                     <MkButton variant="danger" icon="fa-trash"
                                               label={t("mk.avatar.remove")} disabled={disabled}
                                               onClick={() => {
                                                   if (!disabled) {
                                                       onClear?.();
                                                   }
                                               }} />
                                 )}
                                 <MkButton variant="ghost" icon="fa-times" label={t("mk.close")}
                                           onClick={() => setOpen(false)} />
                             </>
                         )}>
                    <div className="mk-avatar-editor-preview">
                        {previewSrc ? <img src={previewSrc} alt={modalTitle} /> : (
                            <div className="mk-avatar-editor-empty">
                                <i className="fa fa-picture-o" />
                                <span>{t("mk.avatar.empty")}</span>
                            </div>
                        )}
                    </div>
                </MkModal>
            )}
        </div>
    );
}
